import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Card, Button, Badge, AsyncBoundary, Notice, useToast,
} from '@kollez/design-system';
import { useOpportunity, useDrivesForOpportunity, useApplyToDrive, useResumes, useProfile360 } from '@/lib/queries';
import { formatLPA, formatIndianDate } from '@kollez/shared';
import { prettyOpportunityType } from '@kollez/shared/enums';

/**
 * /opportunities/:id (F75) — full opportunity page with role, pay, eligibility, match, and a
 * 3-step apply wizard. Mirrors kollez/pages/student/opportunity.html:
 *   layout: two-column detail on top; aside with match + eligibility + sticky Apply CTA
 *   wizard: Check details → Custom questions → Confirm
 */
export function StudentOpportunityDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const opp = useOpportunity(id);
  const drives = useDrivesForOpportunity(id);
  const resumes = useResumes();
  const readiness = useProfile360();
  const apply = useApplyToDrive();
  const toast = useToast();

  const [view, setView] = useState<'detail' | 'form' | 'done'>('detail');
  const [step, setStep] = useState(1);
  const [selectedDriveId, setSelectedDriveId] = useState<string | null>(null);
  const [resumeId, setResumeId] = useState<string>('');
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});
  const [ok1, setOk1] = useState(false);
  const [ok2, setOk2] = useState(false);

  // Step 88 — DRIVE_STATUS is lowercase; the old 'Closed'/'Archived' check never matched
  // anything, so the first drive in the array was always picked regardless of state. A
  // student browsing an opportunity could be shown a closed drive as "the eligible drive"
  // and land in a broken apply-wizard.
  const eligibleDrive = useMemo(
    () => drives.data?.drives.find((d) => d.status !== 'closed' && d.status !== 'cancelled') ?? drives.data?.drives[0],
    [drives.data],
  );
  const driveId = selectedDriveId ?? eligibleDrive?._id ?? null;

  const opportunity = opp.data?.opportunity as NonNullable<typeof opp.data>['opportunity'] & {
    description?: string;
    compensation?: { ctc?: { min?: number; max?: number }; ctcInr?: number };
    eligibility?: { branches?: string[]; minCGPA?: number; maxBacklogs?: number };
    applicationSettings?: { customQuestions?: Array<{ question: string; type: string; required: boolean }>; closesAt?: string };
    location?: string;
    type?: string;
    partner?: { name?: string };
  } | undefined;

  const customQs: Array<{ question: string; type: string; required: boolean }> = opportunity?.applicationSettings?.customQuestions ?? [];

  async function submit() {
    if (!driveId) { toast.show({ tone: 'warning', message: 'No drive attached to this opportunity yet — try again once the TPO opens one.' }); return; }
    try {
      await apply.mutateAsync({
        drive: driveId,
        resume: resumeId || undefined,
        customAnswers: Object.keys(customAnswers).length ? customAnswers : undefined,
      });
      setView('done');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Apply failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  const confirmed = ok1 && ok2;

  return (
    <AsyncBoundary
      kind="detail"
      loading={opp.isLoading}
      error={opp.error}
      empty={!opp.isLoading && !opp.data}
      emptyTitle="Opportunity not found"
      emptyHint="The link may be stale, or this opening has been closed. Head back to Explore for currently open drives."
      onRetry={() => void opp.refetch()}
    >
      {opportunity && view === 'detail' && (
        <div className="stack">
          <div className="page-header">
            <div className="flex gap-4 items-center">
              <span className="w-14 h-14 rounded bg-bg-tertiary text-lg font-semibold grid place-items-center">
                {(opportunity.partner?.name ?? '?').slice(0, 2).toUpperCase()}
              </span>
              <div className="ph-text">
                <p className="eyebrow">
                  {opportunity.partner?.name ?? 'Company'}
                  {opportunity.applicationSettings?.closesAt && ` · closes ${formatIndianDate(opportunity.applicationSettings.closesAt)}`}
                </p>
                <h1 className="h-page">{opportunity.title}</h1>
                <p className="subhead">
                  {opportunity.compensation?.ctc?.min ? formatLPA(opportunity.compensation.ctc.min) : '—'}
                  {opportunity.location ? ` · ${opportunity.location}` : ''}
                  {opportunity.type ? ` · ${prettyOpportunityType(opportunity.type)}` : ''}
                </p>
              </div>
            </div>
          </div>

          {/*
            TODO: student assessment notice. Once Opportunity.workflow carries stages with
            { type: 'assessment', assessmentId, durationMin } refs, render the notice below.
            Shape planned:
              const assessmentStages = opportunity.workflow?.stages?.filter((s) => s.type === 'assessment') ?? [];
              const totalMin = assessmentStages.reduce((n, s) => n + (s.durationMin ?? 0), 0);
              if (assessmentStages.length) render notice with count + totalMin + days-allowed.
            Skipped in Part 4 because the Opportunity API doesn't populate workflow refs yet.
          */}

          <div className="grid gap-5 lg:grid-cols-[1fr_320px] items-start">
            <div className="grid gap-4 min-w-0">
              <Card padding="lg">
                <h2 className="h-section mb-2">The role</h2>
                <p className="whitespace-pre-line leading-relaxed">{opportunity.description || 'No description yet.'}</p>
              </Card>

              {/* Type-aware compensation surface (Ship 5f). Renders whichever fields the
                  opportunity type actually populates: CTC for Placement_*, stipend for
                  Internship, courseFee for Training/Certification/Workshop, prize table
                  for Hackathon. Falls back to Pay/CTC for anything unclassified. */}
              <TypeAwareCompensationCard opportunity={opportunity} />

              {/* Duration is populated for Internship / Training / Workshop / Hackathon /
                  Contract_Position / R&D / Mentorship. Skip for Placement_* where it's null. */}
              <DurationCard opportunity={opportunity} />

              <Card padding="lg">
                <h2 className="h-section mb-2">Drives at your college</h2>
                {drives.isLoading && <div className="text-ink-tertiary text-sm">Loading…</div>}
                {!drives.isLoading && (drives.data?.drives.length ?? 0) === 0 && (
                  <div className="text-ink-tertiary text-sm">No drive scheduled yet.</div>
                )}
                <div className="grid gap-2">
                  {drives.data?.drives.map((d) => (
                    <button
                      key={d._id}
                      onClick={() => setSelectedDriveId(d._id)}
                      className={`text-left p-3 rounded border flex items-baseline justify-between gap-3 ${
                        driveId === d._id ? 'border-ink-accent bg-bg-info/20' : 'border-line-secondary hover:bg-bg-secondary/40'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{d.driveName}</div>
                        <div className="text-xs text-ink-tertiary">
                          {d.college?.name ?? '—'} · {formatIndianDate(d.driveDate)}
                        </div>
                      </div>
                      {/* Step 84 — DRIVE_STATUS is lowercase in the schema; the old check
                          compared against 'Announced' / 'Applications_Open' which never
                          existed in the enum, so every drive rendered the outline tone.
                          A student browsing an opportunity's drives now sees active states
                          in success tone. */}
                      <Badge tone={d.status === 'open' || d.status === 'assessing' || d.status === 'interviewing' || d.status === 'offers' ? 'success' : 'outline'}>{d.status}</Badge>
                    </button>
                  ))}
                </div>
              </Card>
            </div>

            <aside className="grid gap-3 lg:sticky lg:top-4">
              {/* Deep-audit fix (Page 19/N): the old block put a green ✓
                  next to every eligibility line unconditionally, under a
                  header 'You can apply'. If the student's branch, CGPA or
                  backlog count actually failed the criteria, the green
                  checkmark was an outright lie. Renamed to 'Who this is
                  for' and dropped the fabricated ✓ badges — the criteria
                  are now presented as neutral requirements the student can
                  compare themselves against. The Apply button still gates
                  on driveId, and the server-side eligibility gate is the
                  authoritative decision. */}
              <Card padding="md">
                <h3 className="h-subsection mb-2">Who this is for</h3>
                <ul className="grid gap-1 text-sm">
                  {opportunity.eligibility?.branches?.length ? (
                    <li><span className="text-ink-tertiary">Branches:</span> {opportunity.eligibility.branches.join(', ')}</li>
                  ) : null}
                  {typeof opportunity.eligibility?.minCGPA === 'number' && (
                    <li><span className="text-ink-tertiary">Min CGPA:</span> {opportunity.eligibility.minCGPA}</li>
                  )}
                  {typeof opportunity.eligibility?.maxBacklogs === 'number' && (
                    <li><span className="text-ink-tertiary">Max backlogs:</span> {opportunity.eligibility.maxBacklogs}</li>
                  )}
                </ul>
                <p className="helper m-0 mt-2">
                  The placement office confirms your eligibility when you apply.
                </p>
              </Card>

              {readiness.data && (
                <Card padding="md">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-xs text-ink-tertiary">Your readiness</span>
                    <b>{readiness.data.readiness.overall}/100</b>
                  </div>
                  <div className="h-2 bg-bg-tertiary rounded overflow-hidden">
                    <span className="block h-full bg-ink-accent" style={{ width: `${readiness.data.readiness.overall}%` }} />
                  </div>
                  <Link to="/profile-360" className="text-ink-info text-xs underline mt-2 inline-block">See what you can raise →</Link>
                </Card>
              )}

              <Button variant="primary" onClick={() => setView('form')} disabled={!driveId}>Apply now</Button>
              {!driveId && <Notice tone="info">No drive attached at your college yet. Save this and we'll notify you.</Notice>}
            </aside>
          </div>
        </div>
      )}

      {opportunity && view === 'form' && (
        <div className="stack">
          <Button variant="ghost" onClick={() => setView('detail')}>← Back to the drive</Button>

          {/* Stepper */}
          <div className="flex gap-2 flex-wrap">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`px-3 py-1 rounded text-sm ${step === n ? 'bg-bg-info text-ink-inverse' : step > n ? 'bg-bg-success/30 text-ink-secondary' : 'bg-bg-secondary text-ink-tertiary'}`}>
                Step {n}
              </div>
            ))}
          </div>

          {step === 1 && (
            <Card padding="lg">
              <h2 className="h-section mb-3">These go to {opportunity.partner?.name ?? 'the company'}</h2>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mb-4">
                <dt className="text-ink-tertiary">Drive</dt>
                <dd>{drives.data?.drives.find((d) => d._id === driveId)?.driveName ?? '—'}</dd>
              </dl>
              <label className="grid gap-1 mb-3">
                <span className="text-xs uppercase tracking-wide text-ink-tertiary">Resume</span>
                <select className="input" value={resumeId} onChange={(e) => setResumeId(e.target.value)}>
                  <option value="">Use my primary resume</option>
                  {resumes.data?.resumes.map((r) => (
                    <option key={r._id} value={r._id}>{r.name}{r.isPrimary ? ' · primary' : ''}</option>
                  ))}
                </select>
              </label>
              <Notice tone="info">Your phone number is only shared if you're shortlisted.</Notice>
            </Card>
          )}

          {step === 2 && (
            <Card padding="lg">
              <h2 className="h-section mb-3">{opportunity.partner?.name ?? 'The company'} asks</h2>
              {customQs.length === 0 && <div className="text-ink-tertiary">No custom questions.</div>}
              <div className="grid gap-4">
                {customQs.map((q, i) => (
                  <label key={i} className="grid gap-1">
                    <span className="text-sm">{q.question}{q.required && <span className="text-ink-danger"> *</span>}</span>
                    <textarea
                      rows={q.type === 'text' ? 4 : 2}
                      value={customAnswers[q.question] ?? ''}
                      onChange={(e) => setCustomAnswers((prev) => ({ ...prev, [q.question]: e.target.value }))}
                      className="input"
                    />
                  </label>
                ))}
              </div>
            </Card>
          )}

          {step === 3 && (
            <Card padding="lg">
              <h2 className="h-section mb-3">Before you apply</h2>
              <div className="grid gap-2 text-sm">
                <label className="flex gap-2 items-start">
                  <input type="checkbox" checked={ok1} onChange={(e) => setOk1(e.target.checked)} className="mt-1" />
                  <span>The details in my profile are current and accurate.</span>
                </label>
                <label className="flex gap-2 items-start">
                  <input type="checkbox" checked={ok2} onChange={(e) => setOk2(e.target.checked)} className="mt-1" />
                  <span>I understand this application will be visible to my placement office and to {opportunity.partner?.name ?? 'the company'}.</span>
                </label>
              </div>
            </Card>
          )}

          <div className="flex items-center gap-3 mt-2">
            <Button variant="ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>Back</Button>
            <span className="text-xs text-ink-tertiary ml-auto">Step {step} of 3</span>
            {step < 3 ? (
              <Button variant="primary" onClick={() => setStep(step + 1)}>Continue</Button>
            ) : (
              <Button variant="primary" onClick={submit} disabled={!confirmed} loading={apply.isPending}>Apply</Button>
            )}
          </div>
        </div>
      )}

      {view === 'done' && (
        <Card padding="lg" className="grid gap-3 justify-items-start">
          <Badge tone="success">Applied ✓</Badge>
          <h1 className="h-page">Application submitted.</h1>
          <p className="subhead">Your application is with the placement office. You'll get notifications when your status changes.</p>
          <div className="flex gap-2 mt-2">
            <Button variant="primary" onClick={() => nav('/applications')}>My applications</Button>
            <Button variant="ghost" onClick={() => nav('/prep')}>Prep for this</Button>
          </div>
        </Card>
      )}
    </AsyncBoundary>
  );
}

interface TypeAwareOpportunity {
  type?: string;
  compensation?: {
    ctc?: { min?: number; max?: number; currency?: string };
    stipend?: { amount?: number; unit?: 'per_month' | 'total'; currency?: string };
    courseFee?: number;
    prizes?: Array<{ rank: number; amount: number; description?: string; currency?: string }>;
  };
  duration?: {
    months?: number;
    hours?: number;
    days?: number;
    weeks?: number;
    startsAt?: string;
    endsAt?: string;
  };
}

function fmtINR(v: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

function TypeAwareCompensationCard({ opportunity }: { opportunity: TypeAwareOpportunity | undefined }) {
  if (!opportunity) return null;
  const c = opportunity.compensation ?? {};
  const t = opportunity.type ?? '';

  const isPaid = t.startsWith('Placement_') || (!t && c.ctc);
  const isInternship = t === 'Internship' || t === 'Contract_Position';
  const isCourse = t === 'Training' || t === 'Certification' || t === 'Workshop';
  const isHackathon = t === 'Hackathon';

  if (isHackathon && c.prizes && c.prizes.length > 0) {
    return (
      <Card padding="lg">
        <h2 className="h-section mb-2">Prizes</h2>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Rank</th><th>Award</th><th className="r">Amount</th></tr></thead>
            <tbody>
              {c.prizes.slice().sort((a, b) => a.rank - b.rank).map((p) => (
                <tr key={p.rank}>
                  <td><span className="badge">#{p.rank}</span></td>
                  <td>{p.description ?? '—'}</td>
                  <td className="r"><b>{fmtINR(p.amount)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  if (isInternship && c.stipend?.amount) {
    const unit = c.stipend.unit === 'per_month' ? '/month' : ' total';
    return (
      <Card padding="lg">
        <h2 className="h-section mb-2">Stipend</h2>
        <p className="text-lg"><b>{fmtINR(c.stipend.amount)}</b><span className="cell-sub">{unit}</span></p>
      </Card>
    );
  }

  if (isCourse && typeof c.courseFee === 'number') {
    return (
      <Card padding="lg">
        <h2 className="h-section mb-2">Course fee</h2>
        <p className="text-lg">
          {c.courseFee === 0 ? <b>Free</b> : <b>{fmtINR(c.courseFee)}</b>}
        </p>
      </Card>
    );
  }

  if (isPaid || c.ctc) {
    return (
      <Card padding="lg">
        <h2 className="h-section mb-2">Pay</h2>
        <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-sm">
          <dt className="text-ink-tertiary">Annual CTC</dt>
          <dd>
            {c.ctc?.min
              ? (c.ctc.max && c.ctc.max > c.ctc.min
                  ? `${formatLPA(c.ctc.min)} – ${formatLPA(c.ctc.max)}`
                  : formatLPA(c.ctc.min))
              : '—'}
          </dd>
        </dl>
      </Card>
    );
  }
  return null;
}

function DurationCard({ opportunity }: { opportunity: TypeAwareOpportunity | undefined }) {
  const d = opportunity?.duration;
  if (!d) return null;
  const parts: string[] = [];
  if (d.months) parts.push(`${d.months} month${d.months === 1 ? '' : 's'}`);
  if (d.weeks) parts.push(`${d.weeks} week${d.weeks === 1 ? '' : 's'}`);
  if (d.days) parts.push(`${d.days} day${d.days === 1 ? '' : 's'}`);
  if (d.hours) parts.push(`${d.hours} hour${d.hours === 1 ? '' : 's'}`);
  if (parts.length === 0 && !d.startsAt) return null;
  return (
    <Card padding="lg">
      <h2 className="h-section mb-2">Duration</h2>
      <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-sm">
        {parts.length > 0 && (<>
          <dt className="text-ink-tertiary">Length</dt>
          <dd>{parts.join(' · ')}</dd>
        </>)}
        {d.startsAt && (<>
          <dt className="text-ink-tertiary">Starts</dt>
          <dd>{formatIndianDate(d.startsAt)}</dd>
        </>)}
        {d.endsAt && (<>
          <dt className="text-ink-tertiary">Ends</dt>
          <dd>{formatIndianDate(d.endsAt)}</dd>
        </>)}
      </dl>
    </Card>
  );
}
